// App.jsx
// NSFDC Refinance Benefit Response Portal
// Re-designed with Govt of India / Ministry Theme

import React, { useEffect, useState, useRef, useCallback } from "react";

/* -------------------- API BASE -------------------- */
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://otpserver.nsfdcdigital.in";
//http://localhost:5000
//https://refinance-server-356323743278.europe-west1.run.app
//http://190.92.174.111/plesk-site-preview/otpserver.nsfdcdigital.in/
//https://amarranjan0.bsite.net
//https://otpserver.nsfdcdigital.in/
/* -------------------- QUERY HOOK -------------------- */
function useQuery() {
  return new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
}

/* ====================================================
   MAIN COMPONENT
==================================================== */
export default function App() {
  const q = useQuery();
  const mobileFromUrl = q.get("mobileno") || "";

  const [blockedUntil, setBlockedUntil] = useState(null);
  const [blockTimerString, setBlockTimerString] = useState("");

  const formatRetryTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    // Format: "16 Dec 2025, 09:05 PM"
    return date.toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  /* -------------------- STATE -------------------- */
  const [mobile] = useState(() => sanitizeMobile(mobileFromUrl));

  const [userName, setUserName] = useState("");
  const [loanName, setLoanName] = useState("");
  const [businessName, setBusinessName] = useState("");

  // ✅ Stores the interest rate fetched from API, defaults to 6 if empty
  const [interestRate, setInterestRate] = useState("6");

  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [page, setPage] = useState(1); // 1=mobile, 2=otp, 3=confirm, 4=thankyou

  const [statusMsg, setStatusMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ NEW STATE: To hide button if invalid
  const [isInvalid, setIsInvalid] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);

  const [decision, setDecision] = useState("");
  const [alreadyResponded, setAlreadyResponded] = useState(false);

  const otpRef = useRef(null);

  /* -------------------- HELPERS -------------------- */
  function sanitizeMobile(m) {
    return m ? m.replace(/[^0-9]/g, "") : "";
  }

  async function callApi(path, body) {
    try {
      const res = await fetch(`${API_BASE}/api/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return await res.json();
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  }

  /* -------------------- CALLBACKS -------------------- */
  // PAGE 1 – MOBILE VALIDATION
  const checkMobileAllowed = useCallback(async () => {
    setLoading(true);
    // Reset invalid state before checking
    setIsInvalid(false);

    const r = await callApi("check-mobile", { mobile });
    setLoading(false);

    if (!r.allowed) {
      setStatusMsg(
        "Beneficiary Mobile Number is invalid.\nलाभार्थी का मोबाइल नंबर अमान्य है।"
      );
      // ✅ HIDE THE BUTTON
      setIsInvalid(true);
      return;
    }

    setUserName(r.userName);
    setLoanName(r.loanName);
    setBusinessName(r.businessName);

    // ✅ Set interest rate from API response
    if (r.interestRate) setInterestRate(r.interestRate);

    setPage(2);
  }, [mobile]);

  // Check if already responded
  const checkAlreadyResponded = useCallback(async () => {
    const r = await callApi("check-response", { target: mobile });
    if (r && r.ok && r.responded) {
      setAlreadyResponded(true);
      setPage(4);
      setStatusMsg(
        "Response already submitted.\nप्रतिक्रिया पहले ही दर्ज की जा चुकी है।"
      );
    }
  }, [mobile]);

  /* -------------------- EFFECT -------------------- */
  useEffect(() => {
    if (!mobile) {
      setStatusMsg("No mobile number provided.");
      // If no mobile number, effectively invalid, maybe hide button or keep to show error
      setIsInvalid(true);
      return;
    }
    checkAlreadyResponded();
  }, [mobile, checkAlreadyResponded]);

  /* -------------------- OTP FLOW (PAGE 2) -------------------- */
async function sendOtp() {
    setLoading(true);
    setStatusMsg("Sending OTP... / OTP भेजा जा रहा है...");

    const r = await callApi("send-otp", { target: mobile });
    setLoading(false);

    // ✅ CHECK FOR BLOCK DATA IN RESPONSE
    if (r.blockedUntil) {
      setBlockedUntil(r.blockedUntil);
      setBlockTimerString(formatRetryTime(r.blockedUntil));
    }

    if (r.ok) {
      setOtpSent(true);
      if (typeof r.remaining === 'number') setRemainingAttempts(r.remaining);
      
      setStatusMsg(
        `OTP sent successfully. Remaining attempts: ${r.remaining}\nOTP सफलतापूर्वक भेजा गया। शेष प्रयास: ${r.remaining}`
      );
      setTimeout(() => otpRef.current?.focus(), 100);
    } 
    else if (r.error === "blocked") {
      setRemainingAttempts(0);
      // ✅ UPDATED MESSAGE WITH TIME
      const retryTime = formatRetryTime(r.blockedUntil);
      setStatusMsg(
        `You have exceeded the maximum of 3 OTP attempts.\nPlease try again after: ${retryTime}\n\nआपने 3 OTP प्रयासों की अधिकतम सीमा पार कर ली है।\nकृपया ${retryTime} के बाद पुनः प्रयास करें।`
      );
    } 
    else {
      setStatusMsg("Failed to send OTP. Please try again later. / OTP भेजने में विफल।");
    }
  }

  async function verifyOtp() {
    setLoading(true);
    setStatusMsg("Verifying OTP... / OTP की जांच की जा रही है...");

    const r = await callApi("verify-otp", { target: mobile, otp });
    setLoading(false);

    if (typeof r.remaining === "number") setRemainingAttempts(r.remaining);

    // ✅ CHECK FOR BLOCK DATA
    if (r.blockedUntil) {
      setBlockedUntil(r.blockedUntil);
      setBlockTimerString(formatRetryTime(r.blockedUntil));
    }

    if (r.ok) {
      setVerified(true);
      setPage(3);
      setStatusMsg("");
    } else if (r.error === "blocked") {
       // ✅ UPDATED MESSAGE WITH TIME
       const retryTime = formatRetryTime(r.blockedUntil);
       setStatusMsg(
        `OTP attempts exceeded. Please try again after: ${retryTime}\nOTP प्रयासों की सीमा पूरी हो गई है। कृपया ${retryTime} के बाद प्रयास करें।`
      );
    } else {
      setStatusMsg(
        `Incorrect OTP. Remaining attempts: ${r.remaining}\nगलत OTP। शेष प्रयास: ${r.remaining}`
      );
    }
  }
  /* -------------------- SUBMIT (PAGE 3) -------------------- */
  async function submitDecision(value) {
    if (!value) {
      setStatusMsg("Please select an option. / कृपया एक विकल्प चुनें।");
      return;
    }

    setLoading(true);
    setStatusMsg("");

    const r = await callApi("submit-form", {
      target: mobile,
      decision: value,
    });

    setLoading(false);

    if (r.ok) {
      setPage(4);
    } else {
      setStatusMsg("Submission failed. Please try again. / जमा करने में विफल। कृपया पुन: प्रयास करें।");
    }
  }

  /* -------------------- UI -------------------- */
  return (
    <div style={styles.bodyWrapper}>
      {/* GOVT TOP STRIP */}
      <div style={styles.govtStrip}>
        <div style={styles.govtStripInner}>
          <span>भारत सरकार | Government of India</span>
          <span style={styles.ministryText}>सामाजिक न्याय और अधिकारिता मंत्रालय | Ministry of Social Justice and Empowerment</span>
        </div>
      </div>

      {/* MAIN HEADER WITH LOGOS */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          {/* Emblem / Left Logo */}
          <div style={styles.logoGroup}>
            {/* NSFDC Logo */}
            <img
              src="https://ik.imagekit.io/nanoplex/Project%20Image/NSFDC/nsfdclogo-removebg-preview_aCQYROM-P.png"
              alt="NSFDC"
              style={styles.nsfdcLogo}
            />
            <div style={styles.headerTitles}>
              <h1 style={styles.mainTitle}>National Scheduled Castes Finance and Development Corporation</h1>
              <h2 style={styles.subTitle}>Refinance Benefit Response Portal</h2>
            </div>
          </div>

        </div>
      </header>

      {/* NAV BAR (Visual Only) */}
      <div style={styles.navBar}></div>

      {/* MAIN CONTENT CARD */}
      <div style={styles.container}>
        <div style={styles.card}>

          {/* PAGE 1: VERIFY MOBILE */}
          {page === 1 && (
            <div style={styles.fadeStart}>
              <h3 style={styles.sectionHeader}>Beneficiary Verification / लाभार्थी सत्यापन</h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>Mobile Number / मोबाइल नंबर</label>
                <input value={mobile} disabled style={styles.inputDisabled} />
              </div>

              <div style={styles.messageBox}>
                <p style={{ whiteSpace: "pre-line", color: isInvalid ? "#d32f2f" : "#333", fontWeight: isInvalid ? "bold" : "normal" }}>
                  {statusMsg}
                </p>
              </div>

              {/* ✅ HIDE BUTTON IF INVALID */}
              {!isInvalid && (
                <button
                  onClick={checkMobileAllowed}
                  disabled={loading}
                  style={styles.btnPrimary}
                >
                  {loading ? "Verifying..." : "Verify Mobile Number"}
                </button>
              )}
            </div>
          )}

          {/* PAGE 2: OTP */}
{/* PAGE 2: OTP */}
{page === 2 && !alreadyResponded && (
  <div style={styles.fadeStart}>
    <h3 style={styles.sectionHeader}>OTP Verification / OTP सत्यापन</h3>
    <div style={styles.infoRow}>
      <strong>Name:</strong> {userName}
    </div>

    {!otpSent ? (
      /* ------------------------------------------------
         STATE 1: READY TO SEND OTP
      ------------------------------------------------ */
      <>
        {/* Only hide SEND button if blocked */}
        {blockedUntil ? (
           <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#ffebee', borderRadius: '4px', border: '1px solid #d32f2f', marginBottom: 20 }}>
             <h4 style={{ color: '#d32f2f', margin: '0 0 10px 0' }}>OTP Attempt Temporarily Blocked</h4>
             <p style={{ color: '#555', margin: 0 }}>Please try again after:</p>
             <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f', margin: '10px 0' }}>{blockTimerString}</p>
             <p style={{ fontSize: '12px', color: '#777' }}>OTP अस्थायी रूप से अवरुद्ध है। कृपया {blockTimerString} के बाद पुनः प्रयास करें।</p>
           </div>
        ) : (
          <button
            onClick={sendOtp}
            disabled={loading}
            style={styles.btnPrimary}
          >
            {loading ? "Sending..." : "Send OTP"}
          </button>
        )}
      </>
    ) : (
      /* ------------------------------------------------
         STATE 2: VERIFY OTP (User has received SMS)
      ------------------------------------------------ */
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Enter OTP / OTP दर्ज करें</label>
          <input
            ref={otpRef}
            value={otp}
            // ✅ FIX 1: Do NOT disable input if blockedUntil is true. 
            // User must be able to verify the last attempt.
            disabled={verified} 
            placeholder="X X X X"
            onChange={(e) =>
              setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
            }
            style={styles.inputOtp}
          />
        </div>

        {/* Show remaining attempts only if not blocked yet */}
        {remainingAttempts < 3 && !blockedUntil && (
          <p style={styles.errorText}>
            Remaining attempts: {remainingAttempts}
          </p>
        )}

        {/* ✅ FIX 2: Always show Verify Button, even if blockedUntil is set.
            If blocked, show the warning BELOW the button, not INSTEAD of it. */}
        <button
          onClick={verifyOtp}
          disabled={loading} // Always clickable unless loading
          style={styles.btnPrimary}
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </button>

        {/* Show Block Timer Warning BELOW button if they are on their last legs or blocked */}
        {blockedUntil && (
           <div style={{ textAlign: 'center', padding: '15px', marginTop: '15px', backgroundColor: '#ffebee', borderRadius: '4px', border: '1px solid #d32f2f' }}>
             <p style={{ color: '#d32f2f', fontWeight: 'bold', margin: '0 0 5px 0' }}>Maximum attempts reached.</p>
             <p style={{ color: '#333', margin: 0 }}>You cannot generate a new OTP for: <br/><span style={{fontSize: '16px', fontWeight: 'bold'}}>{blockTimerString}</span></p>
             <p style={{ fontSize: '12px', color: '#555', marginTop: 5 }}>आप नया OTP उत्पन्न नहीं कर सकते। कृपया दिए गए OTP को सत्यापित करें।</p>
           </div>
        )}
      </>
    )}

    <p style={{ whiteSpace: "pre-line", marginTop: 15, color: "#d32f2f" }}>
      {statusMsg}
    </p>
  </div>
)}
          {/* PAGE 3: CONFIRMATION (BILINGUAL) */}
          {page === 3 && (
            <div style={styles.fadeStart}>
              <h3 style={styles.sectionHeader}>Confirmation / पुष्टि करें</h3>

              {/* ENGLISH TEXT */}
              <div style={styles.textBoxEnglish}>
                <p>
                  Dear <strong>{userName || "Beneficiary"}</strong>,<br />
                  Your <strong>{loanName}</strong> loan from <strong>{businessName}</strong> is now covered under NSFDC Refinance scheme & the interest rate is reduced to <strong>{interestRate}%</strong>. Please confirm receipt of the reduced interest benefit by selecting one of the below options.
                </p>
              </div>

              {/* HINDI TEXT */}
              <div style={styles.textBoxHindi}>
                <p>
                  नमस्ते <strong>{userName || "लाभार्थी"}</strong> जी,<br />
                <strong>{businessName}</strong> से आपका <strong>{loanName}</strong> लोन अब NSFDC Refinance के तहत परिवर्तित हो गया है और ब्याज दर घटकर <strong>{interestRate}%</strong> कर दी गई है। कृपया नीचे दिए गए विकल्पों में से किसी एक को चुनकर कम ब्याज दर के लाभ की पुष्टि करें।
                </p>
              </div>

              <hr style={styles.divider} />

              {/* RADIO OPTIONS */}
              <div style={styles.radioGroup}>
                <label style={{ ...styles.radioLabel, ...(decision === "Yes" ? styles.radioSelected : {}) }}>
                  <input
                    type="radio"
                    name="decision"
                    value="Yes"
                    checked={decision === "Yes"}
                    onChange={() => setDecision("Yes")}
                    style={styles.radioInput}
                  />
                  <span>YES / हाँ</span>
                </label>

                <label style={{ ...styles.radioLabel, ...(decision === "No" ? styles.radioSelected : {}) }}>
                  <input
                    type="radio"
                    name="decision"
                    value="No"
                    checked={decision === "No"}
                    onChange={() => setDecision("No")}
                    style={styles.radioInput}
                  />
                  <span>NO / नहीं</span>
                </label>
              </div>

              <button
                onClick={() => submitDecision(decision)}
                disabled={loading} // Now the button is clickable even if empty
                style={styles.btnSuccess}
              >
                {loading ? "Submitting..." : "Submit Response / प्रतिक्रिया जमा करें"}
              </button>

              {statusMsg && (
                <p style={{ marginTop: 15, color: "#d32f2f", whiteSpace: "pre-line" }}>
                  {statusMsg}
                </p>
              )}
            </div>
          )}

          {/* PAGE 4: THANK YOU */}
          {page === 4 && (
            <div style={styles.fadeStart}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <span style={{ fontSize: 50, color: 'green' }}>✓</span>
              </div>
              <h3 style={{ ...styles.sectionHeader, textAlign: 'center', color: '#2e7d32' }}>Success / सफल</h3>

              <div style={styles.textBoxEnglish}>
                <p>
                  Thank you for your valuable response.<br />
                  You may visit the NSFDC website to know more about us.
                </p>
              </div>

              <div style={styles.textBoxHindi}>
                <p>
                  आपकी बहुमूल्य प्रतिक्रिया के लिए धन्यवाद।<br />
                  हमारे बारे में अधिक जानने के लिए आप एनएसएफडीसी की वेबसाइट पर जा सकते हैं।
                </p>
              </div>

              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <a href="https://www.nsfdc.nic.in" target="_blank" rel="noreferrer" style={styles.linkButton}>
                  www.nsfdc.nic.in
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* GOVT FOOTER */}
      <footer style={styles.footer}>
        <p>© {new Date().getFullYear()} National Scheduled Castes Finance and Development Corporation. All Rights Reserved.</p>
      </footer>
    </div>
  );
}

/* -------------------- REVISED STYLES -------------------- */
const styles = {
  bodyWrapper: {
    fontFamily: "'Noto Sans', sans-serif",
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  // Top Strip (Govt Standard)
  govtStrip: {
    backgroundColor: "#333",
    color: "#fff",
    fontSize: "12px",
    padding: "8px 0",
  },
  govtStripInner: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "0 20px",
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  ministryText: {
    fontWeight: 'bold',
  },

  // Header
  header: {
    backgroundColor: "#fff",
    borderBottom: "1px solid #ddd",
    padding: "15px 0",
  },
  headerInner: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "0 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "15px",
  },
  logoGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  emblem: {
    height: '70px',
  },
  headerTitles: {
    display: 'flex',
    flexDirection: 'column',
  },
  mainTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#003366", // Govt Blue
    margin: 0,
  },
  subTitle: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#d35400", // Saffron hint
    margin: "4px 0 0 0",
  },
  nsfdcLogo: {
    height: '100px',
  },

  // Visual Nav Bar
  navBar: {
    height: '4px',
    background: 'linear-gradient(90deg, #FF9933 0%, #FFFFFF 50%, #138808 100%)', // Tricolor gradient
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },

  // Content
  container: {
    flex: 1,
    padding: "40px 20px",
    display: "flex",
    justifyContent: "center",
  },
  card: {
    width: "100%",
    maxWidth: "600px",
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "4px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    borderTop: "4px solid #003366",
  },

  sectionHeader: {
    fontSize: "20px",
    color: "#333",
    borderBottom: "2px solid #eee",
    paddingBottom: "10px",
    marginBottom: "20px",
    marginTop: 0,
  },

  // Form Elements
  formGroup: {
    marginBottom: "15px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    fontSize: "14px",
    color: "#555",
  },
  inputDisabled: {
    width: "100%",
    padding: "12px",
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "16px",
    color: "#555",
    boxSizing: 'border-box'
  },
  inputOtp: {
    width: "100%",
    padding: "12px",
    border: "2px solid #003366",
    borderRadius: "4px",
    fontSize: "18px",
    textAlign: "center",
    letterSpacing: "4px",
    boxSizing: 'border-box'
  },

  // Buttons
  btnPrimary: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#003366",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background 0.3s",
  },
  btnSuccess: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#2e7d32", // Green
    color: "#fff",
    fontSize: "16px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginTop: "20px",
  },
  linkButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#003366',
    border: '1px solid #003366',
    textDecoration: 'none',
    fontWeight: 'bold',
    borderRadius: '4px'
  },

  // Text Boxes
  textBoxEnglish: {
    backgroundColor: "#e3f2fd", // Light Blue
    padding: "15px",
    borderRadius: "4px",
    marginBottom: "15px",
    borderLeft: "4px solid #003366",
    lineHeight: "1.6"
  },
  textBoxHindi: {
    backgroundColor: "#fff3e0", // Light Orange
    padding: "15px",
    borderRadius: "4px",
    marginBottom: "15px",
    borderLeft: "4px solid #e65100",
    lineHeight: "1.6"
  },

  // Radio
  radioGroup: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'space-between',
    marginTop: '20px',
  },
  radioLabel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '15px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    backgroundColor: '#fafafa',
  },
  radioSelected: {
    backgroundColor: '#e8f5e9',
    borderColor: '#2e7d32',
    color: '#2e7d32',
    boxShadow: '0 0 0 1px #2e7d32 inset'
  },
  radioInput: {
    marginRight: '10px',
    transform: 'scale(1.2)'
  },

  // Misc
  infoRow: {
    marginBottom: '15px',
    padding: '10px',
    backgroundColor: '#eee',
    borderRadius: '4px',
  },
  errorText: {
    color: '#d32f2f',
    fontWeight: 'bold',
    marginTop: '10px',
    fontSize: '14px'
  },
  divider: {
    margin: "20px 0",
    border: "0",
    borderTop: "1px solid #ddd",
  },
  fadeStart: {
    animation: 'fadeIn 0.5s ease-in',
  },

  footer: {
    backgroundColor: "#003366",
    color: "rgba(255,255,255,0.8)",
    textAlign: "center",
    padding: "15px",
    fontSize: "12px",
  }
};